'use client';

import NextImage from 'next/image';
import type { FC } from 'react';
import cloudflareImageLoader from '@/lib/cloudflare/imageLoader';
import type { TImage } from '@/lib/interfaces/elements/image.types';

function toBase64(input: string): string {
  if (typeof window === 'undefined') {
    // Node
    return Buffer.from(input).toString('base64');
  }
  // Browser
  return window.btoa(input);
}

function defaultShimmer(width: number | `${number}`, height: number | `${number}`): string {
  const w = Math.max(1, Math.round(Number(width) || 24));
  const h = Math.max(1, Math.round(Number(height) || 24));
  const svg = `
    <svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
      <defs>
        <linearGradient id="g">
          <stop stop-color="rgba(0,0,0,0.06)" offset="20%" />
          <stop stop-color="rgba(0,0,0,0.12)" offset="50%" />
          <stop stop-color="rgba(0,0,0,0.06)" offset="70%" />
        </linearGradient>
      </defs>
      <rect width="${w}" height="${h}" fill="rgba(0,0,0,0.06)"/>
      <rect id="r" width="${w}" height="${h}" fill="url(#g)"/>
      <animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="0.9s" repeatCount="indefinite"/>
    </svg>
  `;
  return `data:image/svg+xml;base64,${toBase64(svg)}`;
}

const Image: FC<TImage> = ({
  src,
  height = 100,
  width = 100,
  className,
  alt,
  priority = false,
  fill = false,
  blurDataURL,
  loading,
  fetchPriority,
  quality = 85,
  sizes = '(max-width:768px) 100vw, (max-width:1200px) 100vw, 90vw',
}) => {
  const isSvg = typeof src === 'string' && /\.svg($|\?)/i.test(src);

  // For SVGs, follow Next.js guidance: use a plain <img>, no loader/optimization.
  if (isSvg) {
    if (fill) {
      return (
        <span className='relative block w-full h-full'>
          {/* biome-ignore lint/performance/noImgElement: SVGs bypass next/image per Next.js guidance */}
          <img
            src={src as string}
            alt={alt ?? 'image'}
            className={className}
            loading={loading}
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          />
        </span>
      );
    }
    return (
      // biome-ignore lint/performance/noImgElement: SVGs bypass next/image per Next.js guidance
      <img
        src={src as string}
        alt={alt ?? 'image'}
        className={className}
        loading={loading}
        height={height}
        width={width}
      />
    );
  }

  if (fill) {
    const fallbackBlur = blurDataURL ?? defaultShimmer(24, 24);
    return (
      <NextImage
        loader={cloudflareImageLoader}
        quality={quality}
        sizes={sizes}
        src={src}
        fill={fill}
        priority={priority}
        fetchPriority={fetchPriority}
        placeholder='blur'
        blurDataURL={fallbackBlur}
        alt={alt ?? 'image'}
        className={className}
      />
    );
  }

  const fallbackBlur = blurDataURL ?? defaultShimmer(width, height);
  return (
    <NextImage
      loader={cloudflareImageLoader}
      src={src}
      height={height}
      width={width}
      sizes={sizes}
      placeholder='blur'
      blurDataURL={fallbackBlur}
      className={className}
      alt={alt ?? 'image'}
      priority={priority}
      fetchPriority={fetchPriority}
      quality={quality}
      loading={loading}
    />
  );
};

export default Image;
